// @constants
const actionTypes = require('../raConstants/actionTypes');
const { SUITABILITY_TEST_V6 } = require('constants/flagBasedFeatures');

// UTILITY FUNCTIONS
const findStepAndCategoryIndexes = (
    immWizard,
    wizardIdentifier,
    stepIdentifierOrIndex,
) => {
    return new Promise((resolve, reject) => {
        let identifier = stepIdentifierOrIndex;
        if (typeof stepIdentifierOrIndex === 'number') {
            identifier = immWizard
                .getIn(['persistedWizards', wizardIdentifier, 'categories'])
                .flatMap(x => x.get('steps'))
                .get(stepIdentifierOrIndex)
                .get('identifier');
        }

        let step, catIndex;
        immWizard
            .getIn(['persistedWizards', wizardIdentifier, 'categories'])
            .forEach((category, categoryIndex) => {
                catIndex = categoryIndex;
                step = category
                    .get('steps')
                    .findKey(step => step.get('identifier') === identifier);
                //Continue as long as the step is not found, ImmutableJS forEach, unlike Array.forEach, will stop iterating if the return value is false
                return step == null;
            });

        if (step != null) {
            resolve({ category: catIndex, step });
        } else {
            reject(
                'Could not find step or stepIndex in the categories collection',
            );
        }
    });
};

const findStepIndex = (
    immWizard,
    wizardIdentifier,
    stepIdentifierOrIndexOrSpecial,
) => {
    return new Promise((resolve, reject) => {
        const immSteps = immWizard
            .getIn(['persistedWizards', wizardIdentifier, 'categories'])
            .flatMap(x => x.get('steps'));
        const maxIndex = immSteps.count();
        let stepIndex = maxIndex === 0 ? maxIndex : maxIndex - 1;
        if (typeof stepIdentifierOrIndexOrSpecial !== 'number') {
            if (stepIdentifierOrIndexOrSpecial === '-1') {
                const index = immWizard.getIn([
                    'persistedWizards',
                    wizardIdentifier,
                    'currentStepIndex',
                ]);
                stepIndex = index === 0 ? index : index - 1;
            } else if (stepIdentifierOrIndexOrSpecial === '+1') {
                const index = immWizard.getIn([
                    'persistedWizards',
                    wizardIdentifier,
                    'currentStepIndex',
                ]);
                stepIndex = index + 1 >= maxIndex ? index : index + 1;
            } else {
                stepIndex = immSteps.findKey(
                    x => x.get('identifier') === stepIdentifierOrIndexOrSpecial,
                );
            }
        } else if (stepIdentifierOrIndexOrSpecial < stepIndex) {
            stepIndex = stepIdentifierOrIndexOrSpecial;
        }

        // Using !=, we want type coercion here to check agaisnt null and undefined
        if (stepIndex != null && stepIndex >= 0) {
            resolve(stepIndex);
        } else {
            reject('Error calculating stepIndex');
        }
    });
};

const findCategoryIndex = (
    immWizard,
    wizardIdentifier,
    categoryIdentifierOrIndex,
) => {
    return new Promise((resolve, reject) => {
        let index;
        if (typeof categoryIdentifierOrIndex === 'number') {
            const hasCategory = immWizard
                .getIn(['persistedWizards', wizardIdentifier, 'categories'])
                .get(categoryIdentifierOrIndex);
            if (hasCategory != null) {
                index = categoryIdentifierOrIndex;
            }
        } else {
            index = immWizard
                .getIn(['persistedWizards', wizardIdentifier, 'categories'])
                .findKey(
                    cat => cat.get('identifier') === categoryIdentifierOrIndex,
                );
        }
        //NOTE: we want type coercion here
        if (index != null) {
            resolve(index);
        } else {
            reject('Could not find category in the categories collection');
        }
    });
};

// /UTILITY FUNCTIONS

const initializeWizard = (identifier, categories) => {
    return {
        type: actionTypes.RA_WIZARD_INITIALIZE,
        payload: {
            identifier,
            categories,
        },
    };
};

const toggleExpandInCategoryMenu = (
    wizardIdentifier,
    categoryIdentifierOrIndex,
) => (dispatch, getState) => {
    //NOTE: we want type coercion here
    if (categoryIdentifierOrIndex == null) {
        throw new Error(
            'RAWizard->toggleExpandInCategoryMenu wizardIdentifier categoryIdentifierOrIndex | categoryIdentifierOrIndex Only accepts identifier strings or numbers',
        );
    }
    const immWizard = getState().raWizard;

    findCategoryIndex(
        immWizard,
        wizardIdentifier,
        categoryIdentifierOrIndex,
    ).then(index => {
        dispatch({
            type: actionTypes.RA_WIZARD_TOGGLE_CATEGORY_EXPANDED_VALUE,
            payload: {
                identifier: wizardIdentifier,
                categoryIndex: index,
            },
        });
    });
};

/**
 * Allows the category item in the sidemenu to be expandable on click, not to be confused with `toggleExpandInCategoryMenu`
 *      which controls if the category is expanded or collapsed
 * @param {*} wizardIdentifier
 * @param {*} categoryIdentifierOrIndex
 */
const toggleExpandabilityInCategoryMenu = (
    wizardIdentifier,
    categoryIdentifierOrIndex,
) => (dispatch, getState) => {
    //NOTE: we want type coercion here
    if (categoryIdentifierOrIndex == null) {
        throw new Error(
            'RAWizard->toggleExpandableInCategoryMenu wizardIdentifier categoryIdentifierOrIndex | categoryIdentifierOrIndex Only accepts identifier strings or numbers',
        );
    }
    const immWizard = getState().raWizard;

    findCategoryIndex(
        immWizard,
        wizardIdentifier,
        categoryIdentifierOrIndex,
    ).then(index => {
        dispatch({
            type: actionTypes.RA_WIZARD_TOGGLE_CATEGORY_EXPANDANDABILITY_VALUE,
            payload: {
                identifier: wizardIdentifier,
                categoryIndex: index,
            },
        });
    });
};

const changeStepCompletedStatus = (wizardIdentifier, stepIdentifierOrIndex) => (
    dispatch,
    getState,
) => {
    if (stepIdentifierOrIndex == null) {
        throw new Error(
            'RAWizard->changeStepCompletedStatus wizardIdentifier stepIdentifierOrIndex | stepIdentifierOrIndex Only accepts identifier strings or numbers',
        );
    }
    const immWizard = getState().raWizard;
    findStepAndCategoryIndexes(
        immWizard,
        wizardIdentifier,
        stepIdentifierOrIndex,
    ).then(result => {
        dispatch({
            type: actionTypes.RA_WIZARD_CHANGE_STEP_COMPLETED_VALUE,
            payload: {
                identifier: wizardIdentifier,
                categoryIndex: result.category,
                stepIndex: result.step,
            },
        });
    });
};

const updateWizardStep = (
    wizardIdentifier,
    value,
    category,
    step,
    isCompleted = value,
) => (dispatch, getState) => {
    const isTestV6 = getState().flagBasedFeatures.get(SUITABILITY_TEST_V6);
    dispatch({
        type: actionTypes.RA_WIZARD_STEP_UPDATED,
        payload: {
            wizardIdentifier,
            value,
            category,
            step,
            isCompleted,
            isTestV6,
        },
    });
};

const markStepAsCompleteAndAvanceTo = (
    wizardIdentifier,
    stepIdentifierOrIndexOrSpecial,
) => (dispatch, getState) => {
    if (stepIdentifierOrIndexOrSpecial == null) {
        throw new Error(
            'RAWizard->markStepAsCompleteAndAvanceTo wizardIdentifier stepIdentifierOrIndexOrSpecial | stepIdentifierOrIndexOrSpecial Only accepts identifier strings or numbers',
        );
    }
    const immWizard = getState().raWizard;
    const currentStepIndex = immWizard.getIn([
        'persistedWizards',
        wizardIdentifier,
        'currentStepIndex',
    ]);

    findStepIndex(
        immWizard,
        wizardIdentifier,
        stepIdentifierOrIndexOrSpecial,
    ).then(index => {
        Promise.all([
            findStepAndCategoryIndexes(
                immWizard,
                wizardIdentifier,
                currentStepIndex,
            ),
            findStepAndCategoryIndexes(immWizard, wizardIdentifier, index),
        ]).then(([currentStep, result]) => {
            dispatch({
                type: actionTypes.RA_WIZARD_MARK_COMPLETED_AND_CHANGE_STEP,
                payload: {
                    identifier: wizardIdentifier,
                    current: {
                        category: currentStep.category,
                        step: currentStep.step,
                    },
                    next: {
                        category: result.category,
                        step: result.step,
                    },
                },
            });
        });
    });
    //ignore rejection, the promise is rejected if the requested step is out of bounds or the same as the current step
};

const changeStepTo = (wizardIdentifier, stepIdentifierOrIndexOrSpecial) => (
    dispatch,
    getState,
) => {
    if (stepIdentifierOrIndexOrSpecial == null) {
        throw new Error(
            'RAWizard->changeStepTo wizardIdentifier stepIdentifierOrIndexOrSpecial | stepIdentifierOrIndexOrSpecial Only accepts identifier strings or numbers',
        );
    }
    const immWizard = getState().raWizard;
    const currentStepIndex = immWizard.getIn([
        'persistedWizards',
        wizardIdentifier,
        'currentStepIndex',
    ]);

    findStepIndex(
        immWizard,
        wizardIdentifier,
        stepIdentifierOrIndexOrSpecial,
    ).then(index => {
        Promise.all([
            findStepAndCategoryIndexes(
                immWizard,
                wizardIdentifier,
                currentStepIndex,
            ),
            findStepAndCategoryIndexes(immWizard, wizardIdentifier, index),
        ]).then(([currentStep, result]) => {
            dispatch({
                type: actionTypes.RA_WIZARD_CHANGE_STEP,
                payload: {
                    identifier: wizardIdentifier,
                    current: {
                        category: currentStep.category,
                        step: currentStep.step,
                    },
                    next: {
                        category: result.category,
                        step: result.step,
                    },
                },
            });
        });
    });
    //ignore rejection, the promise is rejected if the requested step is out of bounds or the same as the current step
};

const showModal = (identifier, value) => ({
    type: actionTypes.RA_WIZARD_SHOW_MODAL,
    payload: {
        identifier,
        value,
    },
});

const setTotalAnswers = totalAnswers => ({
    type: actionTypes.RA_WIZARD_SET_TOTAL_ANSWERS,
    payload: totalAnswers,
});

const showFirstAnswerModal = (identifier, value) => ({
    type: actionTypes.RA_WIZARD_SHOW_FIRST_ANSWER_MODAL,
    payload: {
        identifier,
        value,
    },
});

const shouldDisplayModal = (identifier, value, category, step) => ({
    type: actionTypes.RA_WIZARD_SHOULD_DISPLAY_MODAL,
    payload: {
        identifier,
        value,
        category,
        step,
    },
});

const reset = () => ({
    type: actionTypes.RA_WIZARD_RESET,
});

module.exports = {
    initializeWizard,
    findStepAndCategoryIndexes,
    markStepAsCompleteAndAvanceTo,
    changeStepTo,
    changeStepCompletedStatus,
    setTotalAnswers,
    shouldDisplayModal,
    showModal,
    showFirstAnswerModal,
    toggleExpandInCategoryMenu,
    toggleExpandabilityInCategoryMenu,
    updateWizardStep,
    reset,
};
